<script setup lang="ts">
import {storeToRefs} from "pinia";
import {useNamaFlagsStore} from "@/stores/nama-flags-store.ts";
import {computed, nextTick, onMounted, ref, watch} from "vue";
import {QInfiniteScroll} from "quasar";
import ChatPresenter from "@/utils/presenters/ChatPresenter.ts";
import NamaChatInput from "@/components/navBar/chat/NamaChatInput.vue";
import NamaChatMessage from "@/components/navBar/chat/NamaChatMessage.vue";
import type {PreviewItem} from "@/components/navBar/chat/ChatMediaPreviewerDialog.vue";
import ChatMediaPreviewerDialog from "@/components/navBar/chat/ChatMediaPreviewerDialog.vue";
import ChatUtils from "@/utils/common/ChatUtils.ts";
import {DisplayEntry, MessageMetadata} from "@/utils/presenters/interfaces.ts";
import NamaIcon from "@/components/icons/NamaIcon.vue";
import NamaLabel from "@/components/NamaLabel.vue";
import Translator from "@/utils/common/Translator.ts";

const props = defineProps<{
  presenter: ChatPresenter;
  isOtherUserOnline: boolean;
}>();

const emit = defineEmits<{
  toggleChatInfo: [];
}>();

const {darkMode} = storeToRefs(useNamaFlagsStore());
const infiniteScrollRef = ref<QInfiniteScroll | null>(null);
const scrollContainerRef = ref<HTMLElement | null>(null);
const previewerVisible = ref(false);
const previewerItems = ref<PreviewItem[]>([]);
const previewerIndex = ref(0);
const hasUserScrolled = ref(false);
const isSwitchingChat = ref(false);

watch(() => props.presenter.currentChatMetadata.value?.id, async () => {
  isSwitchingChat.value = true;
  hasUserScrolled.value = false;
  requestAnimationFrame(() => {
    isSwitchingChat.value = false;
  });
});

watch(() => props.presenter.otherUserTyping.value, async (isTyping) => {
  if (isTyping && !props.presenter.hasMoreAfter.value) {
    await props.presenter.scrollToBottom();
  }
});

onMounted(() => {
  props.presenter.setChatContainerRef(scrollContainerRef);
});

async function onContainerScroll(e: Event) {
  if (props.presenter.isAutoScrolling.value && isSwitchingChat.value)
    return;
  const el = e.target as HTMLElement;
  const isNearTop = el.scrollTop <= 0;
  if (isNearTop && !hasUserScrolled.value) {
    hasUserScrolled.value = true;
    infiniteScrollRef.value?.resume();
  }
  const isNearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 50;
  if (isNearBottom && props.presenter.hasMoreAfter.value && !props.presenter.isLoadingMoreAfter.value) {
    await props.presenter.loadNewerMessages();
  }
}

async function onLoad(_index: number, done: (stop?: boolean) => void) {
  if (!hasUserScrolled.value || !props.presenter.hasMoreMessages.value) {
    done(true);
    return;
  }
  const el = props.presenter.chatContainerRef.value!;
  const prevHeight = el.scrollHeight;
  const loaded = await props.presenter.loadOlderMessages();
  await nextTick();
  const newHeight = el.scrollHeight;
  el.scrollTop += (newHeight - prevHeight - 200);
  hasUserScrolled.value = false;
  done(!loaded || !props.presenter.hasMoreMessages.value);
}

const displayEntries = computed<DisplayEntry[]>(() => {
  const messages = props.presenter.messageMetadataList.value;
  if (!messages || messages.length === 0)
    return [];
  const entries: DisplayEntry[] = [];
  let i = 0;
  while (i < messages.length) {
    const msg = messages[i];
    if (isAlbumCandidate(msg)) {
      const albumMessages: MessageMetadata[] = [msg];
      let j = i + 1;
      while (j < messages.length && isAlbumCandidate(messages[j])
      && messages[j].sender?.id === msg.sender?.id) {
        albumMessages.push(messages[j]);
        j++;
      }
      if (albumMessages.length >= 2) {
        entries.push({type: 'album', messages: albumMessages});
        i = j;
        continue;
      }
    }
    entries.push({type: 'single', messages: [msg]});
    i++;
  }
  return entries;
});

function isAlbumCandidate(msg: MessageMetadata): boolean {
  if (!msg.attachment?.attachmentInfo) return false;
  const type = ChatUtils.resolveEffectiveMessageType(msg);
  return (type === 'Image' || type === 'Video') && !msg.messageContent?.trim();
}

function shouldShowDateLabelForEntry(entryIndex: number): boolean {
  if (entryIndex === 0) return true;
  const entries = displayEntries.value;
  const currentMsg = entries[entryIndex].messages[0];
  const prevEntry = entries[entryIndex - 1];
  const prevMsg = prevEntry.messages[prevEntry.messages.length - 1];
  return ChatUtils.shouldShowDateLabel(currentMsg, prevMsg);
}

function getDateLabelForEntry(entryIndex: number): string {
  return ChatUtils.getChatDateLabel(displayEntries.value[entryIndex].messages[0].sentAt);
}

function openPreview(src: string, fileName: string) {
  const ext = ChatUtils.getFileExtension(fileName);
  previewerItems.value = [{src, fileName, isVideo: ChatUtils.VIDEO_EXTENSIONS.has(ext)}];
  previewerIndex.value = 0;
  previewerVisible.value = true;
}

function openAlbumPreview(messages: MessageMetadata[], startIndex: number) {
  previewerItems.value = messages.map(msg => {
    const fileId = msg.attachment?.attachmentInfo?.fileId;
    const fileName = msg.attachment?.attachmentInfo?.fileName ?? '';
    const ext = ChatUtils.getFileExtension(fileName);
    return {
      src: ChatUtils.getFileDownloadUrl(fileId),
      fileName,
      isVideo: ChatUtils.VIDEO_EXTENSIONS.has(ext),
    };
  });
  previewerIndex.value = startIndex;
  previewerVisible.value = true;
}

function onPreviewerClose() {
  previewerItems.value = [];
  previewerIndex.value = 0;
}

function openUserChat(userId: string) {
  const user = props.presenter.findUserById(userId);
  if (user) props.presenter.createConversationAndOpen('Direct', [user]);
}

</script>

<template>
  <q-card-section
    class="tw-w-full tw-h-fit tw-self-center tw-items-center tw-flex tw-flex-row tw-p-1 dark:tw-bg-namaBackground-dim-dark tw-bg-namaBackground-dim-light">
    <q-toolbar class="tw-h-10 tw-w-full">
      <q-btn unelevated no-caps dense
             @click="emit('toggleChatInfo')"
             :title="Translator.translateId('GroupInfo')"
             class="tw-px-4 tw-rounded-md tw-text-namaFontColor-gray dark:tw-text-namaFontColor-dark tw-w-full tw-justify-between">
        <q-avatar v-if="presenter.currentChatMetadata.value?.chatConversationType === 'Direct'" size="48px"
                  color="primary" class="tw-shadow-md dark:tw-shadow-gray-900">
          <img :src="presenter.currentChatMetadata.value?.imageSrc ?? ''" alt=""/>
        </q-avatar>
        <q-toolbar-title class="tw-w-full tw-text-primary-light dark:tw-text-primary-dark tw-text-base tw-justify-items-start">
          <div class="tw-flex tw-flex-col tw-w-full">
            <span v-text="presenter.currentChatMetadata.value?.chatTitle ?? ''" class="tw-text-start tw-w-[95%] tw-max-w-[95%] tw-line-clamp-1"/>
            <NamaLabel v-if="presenter.currentChatMetadata.value?.chatConversationType === 'Direct'"
                       :text="isOtherUserOnline ? 'activeNow' : 'notActive'"
                       class="!tw-text-gray-400 !tw-text-sm">
              <NamaIcon defaultIcon="circle" class="!tw-text-[8px]"
                        :class="[isOtherUserOnline ? 'active-green' : 'active-gray']"/>
            </NamaLabel>
            <NamaLabel v-else :text="presenter.currentChatMetadata.value?.chatParticipantsTitle ?? ''"
                       class="!tw-text-gray-400 !tw-text-sm tw-text-start tw-w-[95%] tw-max-w-[95%] tw-line-clamp-1">
            </NamaLabel>
          </div>
        </q-toolbar-title>
        <q-avatar v-if="presenter.currentChatMetadata.value?.chatConversationType === 'Group'" size="48px" color="primary">
          <NamaIcon defaultIcon="group" class="tw-size-12 active-white"/>
        </q-avatar>
      </q-btn>
    </q-toolbar>
  </q-card-section>
  <q-card-section class="tw-w-full tw-grow tw-overflow-hidden tw-p-0">
    <div class="tw-flex tw-w-full tw-h-full tw-mx-auto">
      <div class="tw-flex-1 tw-flex tw-flex-col tw-h-full tw-overflow-y-auto tw-overflow-x-hidden" ref="scrollContainerRef"
           @scroll="onContainerScroll">
        <q-infinite-scroll @load="onLoad" reverse ref="infiniteScrollRef" :disable="!presenter.initialLoadDone.value" class="tw-h-full">
          <template v-slot:loading>
            <div class="row justify-center q-my-md">
              <q-spinner color="primary" name="dots" size="40px"/>
            </div>
          </template>
          <div class="tw-h-full tw-w-[80%] tw-justify-self-center">
            <template v-for="(entry, entryIndex) in displayEntries" :key="entryIndex">
              <q-chat-message v-if="shouldShowDateLabelForEntry(entryIndex)"
                              class="dark:tw-text-white"
                              :label="getDateLabelForEntry(entryIndex)"/>
              <NamaChatMessage v-if="entry.type === 'album'"
                               :message="entry.messages[0]"
                               :album-messages="entry.messages"
                               :chatType="presenter.currentChatMetadata.value?.chatConversationType ?? ''"
                               :current-user-id="presenter.currentUserInfo?.id"
                               @preview="openPreview"
                               @preview-album="openAlbumPreview"
                               @open-user-chat="openUserChat"/>
              <NamaChatMessage v-else
                               v-for="message in entry.messages" :key="message.id"
                               :message="message"
                               :chatType="presenter.currentChatMetadata.value?.chatConversationType ?? ''"
                               :current-user-id="presenter.currentUserInfo?.id"
                               @preview="openPreview"
                               @open-user-chat="openUserChat"/>
            </template>
            <!-- Typing indicator -->
            <q-chat-message v-if="presenter.otherUserTyping.value" :sent="false"
                            :text-color="darkMode ? 'white' : 'black'" :bg-color="darkMode ? 'grey-9' : 'grey-4'">
              <q-spinner-dots size="2rem"/>
            </q-chat-message>
          </div>
        </q-infinite-scroll>
      </div>
    </div>
  </q-card-section>
  <q-card-section class="tw-w-full tw-h-fit tw-self-center tw-items-center tw-flex tw-flex-row">
    <NamaChatInput v-if="presenter.isCurrentUserParticipant" :presenter="presenter"/>
    <div v-else class="tw-w-full tw-flex tw-items-center tw-justify-center tw-gap-2 tw-py-3 tw-px-4
                        tw-text-sm tw-text-gray-600 dark:tw-text-gray-400 tw-bg-red-200 dark:tw-bg-red-900/20 tw-rounded-xl tw-border tw-border-red-500 dark:tw-border-red-800/40">
      <NamaIcon defaultIcon="block" class="!tw-text-lg tw-text-red-400"/>
      <span v-text="Translator.translateId('chatNotMember')"/>
    </div>
  </q-card-section>
  <ChatMediaPreviewerDialog v-model="previewerVisible" v-model:current-index="previewerIndex"
                            :items="previewerItems" @close="onPreviewerClose"/>
</template>
